@use '../../../sass/theme';
@use '../../../sass/form-control-mixins';

/**
* theme options
* @prop --jds-theme-input-fs: Default (desktop) input font size
* @prop --jds-theme-input-fs-lg: Mobile (larger) input font size
* @prop --jds-theme-input-fs-sm: Smaller input font size
* @prop --jds-theme-input-fs-label-data: Additional label data text size

* @prop --jds-theme-color-input-bg: Input background colour
* @prop --jds-theme-color-input-bg-disabled: Input disabled background colour
* @prop --jds-theme-color-input-bg-readonly: Input readonly background colour

* @prop --jds-theme-color-input-text: Input text colour
* @prop --jds-theme-color-input-text-placeholder: Input placeholder text colour
* @prop --jds-theme-color-input-text-disabled: Input disabled text colour
* @prop --jds-theme-color-input-text-readonly: Input readonly text colour

* @prop --jds-theme-color-input-clear-icon: Input clear icon colour
* @prop --jds-theme-color-input-border: Input border colour
* @prop --jds-theme-color-label-text: Input label text colour
* @prop --jds-theme-color-focus-outline: Input border focus colour
*/

:host {
  display: block;

  .jui-control {
    @include form-control-mixins.jui-control-base;

    > * {
      flex: 1 100%;
    }

    &__input-wrapper {
      display: flex;
      position: relative;
    }

    &__label {
      @include form-control-mixins.form-control-label;
      &--disabled {
        @include form-control-mixins.form-label-disabled;
      }

      &--narrower {
        @include form-control-mixins.form-control-label-narrower;
      }
    }

    &__label-text {
      @include form-control-mixins.form-control-label-text;
    }

    &__help {
      @include form-control-mixins.form-control-help;
    }

    &__tooltip {
      @include form-control-mixins.form-control-tooltip;

      &--input-sm {
        @include form-control-mixins.form-control-tooltip-input-sm;
      }

      &--input-lg {
        @include form-control-mixins.form-control-tooltip-input-lg;
      }
    }

    &__tooltip-text {
      @include form-control-mixins.form-control-tooltip-text;

      &--left {
        @include form-control-mixins.form-control-tooltip-text-left;
      }

      &--left-bottom {
        @include form-control-mixins.form-control-tooltip-text-left-bottom;
      }
    }

    &__tooltip-text::before {
      @include form-control-mixins.form-control-tooltip-text-before;
    }

    &__tooltip-text--left::before {
      @include form-control-mixins.form-control-tooltip-text-left-before;
    }

    &__tooltip-text--left-bottom::before {
      @include form-control-mixins.form-control-tooltip-text-left-bottom-before;
    }

    &__tooltip:hover .jui-control__tooltip-text {
      @include form-control-mixins.tooltip-hover;
    }

    &__label-step {
      margin: 0 var(--jds-space-xs);
      font-size: var(--jui-input-fs-label-data);
      color: var(--jui-primary-color);
    }

    &__label-min-max {
      margin: 0 var(--jds-space-xs);
      font-size: var(--jui-input-fs-label-data);
    }

    &__label--highlight {
      color: var(--jui-primary-color);
    }

    &__icon {
      line-height: 1.1;
      justify-content: center;
      position: absolute;
      align-items: center;
      border-top-left-radius: var(--jui-border-radius);
      border-bottom-left-radius: var(--jui-border-radius);
      border-right: 1px solid var(--jui-input-color-border);
      top: 3px;
      padding: 4px 8px;
    }

    &__reset {
      line-height: 1.1;
      position: absolute;
      right: 75px;
      cursor: pointer;
      top: 5px;
      padding: 4px;
    }

    &__stepper {
      line-height: 1.1;
      position: absolute;
      display: flex;
      right: 0;
      cursor: pointer;
      top: 3px;
      border-left: 1px solid var(--jui-input-color-border);
    }

    &__step {
      padding: 4px 8px;
    }

    &__error {
      @include form-control-mixins.form-control-error;
    }

    &__input {
      @include form-control-mixins.form-control-input-base;
      flex: auto;
      height: 40px;
      padding-right: 80px;

      &:disabled {
        @include form-control-mixins.form-control-disabled;
      }

      &:read-only {
        @include form-control-mixins.form-control-readonly;
      }

      &::placeholder {
        color: var(--jui-input-color-placeholder);
      }

      &--formatting {
        transform: scale3d(0, 0, 0);
      }

      &--has-focus {
        @include form-control-mixins.form-control-focused;

        & .jui-control__input--invalid {
          border-color: var(--jui-danger-color);
          &:focus-visible {
            outline-color: var(--jui-danger-color);
          }
        }
      }

      &--clearable {
        padding-right: 100px;
      }

      &--has-icon {
        padding-left: 50px;
      }

      &--invalid {
        border-color: var(--jui-danger-color);
        &:focus-visible {
          outline-color: var(--jui-danger-color);
        }
      }
    }

    &:focus-within {
      .jui-control__input {
        @include form-control-mixins.form-control-focused;

        &--invalid {
          border-color: var(--jui-danger-color);
          &:focus-visible {
            outline-color: var(--jui-danger-color);
          }
        }
      }
    }

    // TRANSITIONAL MODIFIER
    &--transitional {
      .jui-control__label {
        top: -26px;
        @include form-control-mixins.smooth-font-transforms;
        transform: translate(9px, 35px) scale(1);
        transition: transform 0.1s ease-in-out;
        user-select: none;
        pointer-events: none;

        &--has-icon {
          transform: translate(51px, 35px) scale(1);
        }
      }

      .jui-control__input {
        &::placeholder {
          opacity: 0;
        }

        &:focus::placeholder {
          opacity: 1;
          animation-delay: 0.2s;
        }
      }

      .jui-control__label--has-value,
      .jui-control__label--has-focus {
        @include form-control-mixins.smooth-font-transforms;
        transform: translate(-12%, 4px) scale(0.75);
        transition: transform 0.2s ease-out;
      }
    }

    // SIZE MODIFIERS
    &--lg {
      font-size: var(--jui-input-fs-lg);
      line-height: calc(var(--jui-input-fs-lg) * 1.5) !important;
      & .jui-control__icon {
        top: 7px;
      }

      & .jui-control__stepper {
        top: 7px;
      }

      & .jui-control__reset {
        top: 8px;
        right: 100px;
      }

      & .jui-control__error {
        font-size: var(--jui-input-fs-lg);
      }

      & .jui-control__input {
        height: 60px;

        &.jui-control__input--clearable {
          padding-right: 148px;
        }

        &.jui-control__input--has-icon {
          padding-left: 65px;
        }
      }

      &.jui-control--transitional {
        & .jui-control__label {
          transform: translate(9px, 47px) translateZ(0) scale(1);
        }

        & .jui-control__label--has-icon {
          transform: translate(65px, 47px) translateZ(0) scale(1);
        }

        & .jui-control__label--has-value,
        & .jui-control__label--has-focus {
          @include form-control-mixins.smooth-font-transforms;
          transform: translate(-12%, 4px) scale(0.75);
          transition: transform 0.2s ease-out;
        }
      }
    }

    &--sm {
      font-size: var(--jui-input-fs-sm);
      line-height: calc(var(--jui-input-fs-sm) * 1.5) !important;
      & .jui-control__icon {
        top: 2px;
      }

      & .jui-control__reset {
        top: 3px;
        right: 70px;
      }

      & .jui-control__stepper {
        top: 3px;
        line-height: 0.5;
      }

      & .jui-control__input {
        height: 30px;
        font-size: var(--jui-input-fs-sm);

        &.jui-control__input--has-icon {
          padding-left: 40px;
        }
      }

      &.jui-control--transitional {
        & .jui-control__label {
          transform: translate(9px, 28px) translateZ(0) scale(1);
        }
        & .jui-control__label--has-icon {
          transform: translate(40px, 28px) translateZ(0) scale(1);
        }

        & .jui-control__label--has-value,
        & .jui-control__label--has-focus {
          @include form-control-mixins.smooth-font-transforms;
          transform: translate(-10%, 4px) scale(0.8);
          transition: transform 0.2s ease-out;
        }
      }
    }

    // ALIGN MODIFIER
    &--align-right {
      .jui-control__input {
        text-align: right;
      }
    }
  }
}
